/**
 * Nonce Loader - Cache-Safe Nonce Management
 *
 * Fetches fresh nonces from REST endpoint on page load.
 * This solves the full-page cache problem where inline nonces expire.
 *
 * Flow:
 * 1. Page loads (may be from cache, no nonces inline)
 * 2. This script fetches fresh nonces from /wp-json/inpost-pay/v1/nonces
 * 3. Nonces exposed as window.inpostPayNonces (backward compatible)
 * 4. Other scripts can use nonces normally
 *
 * API:
 * - window.inpostPayNonces - Nonce object (populated after load)
 * - window.inpostPayWaitForNonces(callback) - Wait for nonces before executing
 *
 * @since 2.0.11
 */

(function () {
  'use strict';

  // Queue for callbacks waiting for nonces
  let waitQueue = [];
  let noncesReady = false;

  /**
   * Public API: Wait for nonces to load before executing callback.
   *
   * Usage:
   *   window.inpostPayWaitForNonces(() => {
   *     // Safe to use window.inpostPayNonces here
   *     fetch(url, { body: new FormData().append('_ajax_nonce', window.inpostPayNonces.payBind) });
   *   });
   *
   * @param {Function} callback - Function to execute when nonces ready
   */
  window.inpostPayWaitForNonces = function(callback) {
    if (noncesReady) {
      // Nonces already loaded - execute immediately
      callback();
    } else {
      // Nonces not ready - queue callback
      waitQueue.push(callback);
    }
  };

  /**
   * Execute all queued callbacks.
   */
  function flushWaitQueue() {
    noncesReady = true;
    while (waitQueue.length > 0) {
      const callback = waitQueue.shift();
      try {
        callback();
      } catch (error) {
        console.error('[InPostPay] Error in nonce wait callback:', error);
      }
    }
  }

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadNonces);
  } else {
    loadNonces();
  }

  /**
   * Load fresh nonces from REST endpoint.
   */
  async function loadNonces() {
    const config = window.inpostPayConfig;

    if (!config || !config.nonceEndpoint) {
      console.error('[InPostPay] Nonce endpoint not configured');
      return;
    }

    try {
      const response = await fetch(config.nonceEndpoint, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Expose nonces globally (backward compatible with existing code)
      window.inpostPayNonces = data.nonces || {};

      // Mark as loaded
      if (config) {
        config.noncesLoaded = true;
      }

      // Dispatch event for scripts waiting for nonces
      document.dispatchEvent(
        new CustomEvent('inpostPayNoncesLoaded', {
          detail: { nonces: data.nonces },
        })
      );

      // Execute queued callbacks
      flushWaitQueue();
    } catch (error) {
      console.error('[InPostPay] Failed to load nonces:', error);

      // Fallback: empty nonces object (better than undefined)
      window.inpostPayNonces = {};

      if (config) {
        config.noncesLoaded = false;
      }
    }
  }
})();
